/* eslint-disable react-hooks/exhaustive-deps */
import { Dispatch, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { useDropLocations } from "../../store/useDropLocations";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAutoAnimate } from "@formkit/auto-animate/react";

const DEFAULT_LOCATION_CODES = ["VLS", "VBS", "EDP", "SMA"];
const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";

interface SortableChipProps {
  id: string;
  label: string;
  onRemove: (id: string) => void;
}

function SortableChip({ id, label, onRemove }: SortableChipProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      type="button"
      onClick={() => onRemove(id)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-satoshi text-gray-800 cursor-pointer transition-colors touch-none ${
        isDragging
          ? "bg-white border-droppie-oranje shadow-lg z-50 opacity-90"
          : "bg-gray-100 border-gray-200 hover:bg-gray-200"
      }`}
      aria-label={`Remove ${label}`}
    >
      <span
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab active:cursor-grabbing mr-0.5 text-gray-500 hover:text-gray-700 select-none"
        aria-hidden
      >
        ⋮⋮
      </span>
      {label}
      <span className="ml-0.5 text-gray-500">×</span>
    </button>
  );
}

interface Props {
  filterType: "location" | "city";
  setFilterType: Dispatch<React.SetStateAction<"location" | "city">>;
  filterPlaces: string[];
  setFilterPlaces: Dispatch<React.SetStateAction<string[]>>;
  sticky?: boolean;
}

export function VolumesGeneralFilters({
  filterType,
  setFilterType,
  filterPlaces,
  setFilterPlaces,
  sticky = true,
}: Props) {
  const [filtersRef] = useAutoAnimate();
  const { dropLocations, getDropLocations } = useDropLocations();
  const hasSetDefault = useRef(false);

  /** Unique cities from locations */
  const cityOptions = useMemo(() => {
    if (!dropLocations?.length) return [];
    const cities = Array.from(
      new Set(
        dropLocations
          .map((loc) => loc.city)
          .filter((c): c is string => Boolean(c))
      )
    );
    return cities.sort((a, b) => a.localeCompare(b));
  }, [dropLocations]);

  /** Location options (cost_place) */
  const locationOptions = useMemo(() => {
    if (!dropLocations?.length) return [];
    return dropLocations.map((loc) => loc.cost_place as string).filter(Boolean);
  }, [dropLocations]);

  /** Options for the second dropdown based on filter type */
  const placeOptions = filterType === "city" ? cityOptions : locationOptions;

  /** Selected labels for checkbox state */
  const selectedPlaceLabels = useMemo(() => {
    if (filterType === "city") {
      return filterPlaces.filter((p) => cityOptions.includes(p));
    }
    return filterPlaces
      .map((id) => dropLocations?.find((l) => l._id === id)?.cost_place)
      .filter(Boolean) as string[];
  }, [filterType, filterPlaces, dropLocations, cityOptions]);

  /** Selected items with display labels for tag rendering (VLS-FS format for locations) */
  const selectedTags = useMemo(() => {
    const tags =
      filterPlaces
        .filter((id) => id !== TOTAL_ALL_STORES_ID)
        .map((id) => {
          if (filterType === "city") return { id, label: id };
          const loc = dropLocations?.find(
            (l) => l._id === id || l.cost_place === id
          );
          const shortCode = loc
            ? ((loc.cost_place ?? "")
                .split("_")
                .find((t) => /^[A-Z]{3,4}$/.test(t)) ?? "")
            : "";
          const concept = loc?.conceptType?.toString().toUpperCase() ?? "";
          const label = concept ? `${shortCode}-${concept}` : shortCode || id;
          return { id, label };
        }) ?? [];

    if (filterPlaces.includes(TOTAL_ALL_STORES_ID)) {
      tags.push({ id: TOTAL_ALL_STORES_ID, label: "Total all stores" });
    }
    return tags;
  }, [filterType, filterPlaces, dropLocations]);

  /** Effects */
  useEffect(() => {
    getDropLocations();
  }, []);

  /** Set default once: one location per city (VLS, VBS, EDP, SMA) */
  useEffect(() => {
    if (!dropLocations?.length || hasSetDefault.current) return;
    hasSetDefault.current = true;
    const defaultLocations = DEFAULT_LOCATION_CODES.flatMap((code) => {
      const loc = dropLocations.find((l) => {
        const tokens = (l.cost_place ?? "").split("_");
        return tokens.some((t) => /^[A-Z]{3,4}$/.test(t) && t === code);
      });
      return loc?._id ? [loc._id] : [];
    });
    if (defaultLocations.length > 0 && filterPlaces.length === 0) {
      setFilterType("location");
      setFilterPlaces(defaultLocations);
    }
  }, [dropLocations]);

  /** When switching type: select all sub-selections of the new type */
  const prevFilterType = useRef(filterType);
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevFilterType.current = filterType;
      return;
    }
    if (prevFilterType.current !== filterType) {
      prevFilterType.current = filterType;
      if (filterType === "city") {
        setFilterPlaces([...cityOptions]);
      } else {
        const allIds =
          dropLocations?.map((l) => l._id as string).filter(Boolean) ?? [];
        setFilterPlaces(allIds);
      }
    }
  }, [filterType, cityOptions, dropLocations]);

  const onSelectPlace = (option: string) => {
    if (filterType === "city") {
      setFilterPlaces((prev) =>
        prev.includes(option)
          ? prev.filter((p) => p !== option)
          : [...prev, option]
      );
    } else {
      const loc = dropLocations?.find((l) => l.cost_place === option);
      if (!loc?._id) return;
      setFilterPlaces((prev) =>
        prev.includes(loc._id as string)
          ? prev.filter((id) => id !== loc._id)
          : [...prev, loc._id as string]
      );
    }
  };

  const getPlaceLabel = () => {
    const realCount = filterPlaces.filter(
      (p) => p !== TOTAL_ALL_STORES_ID
    ).length;
    if (realCount === 0) return "Select";
    return `${realCount} selected`;
  };

  const onRemoveTag = (placeIdOrName: string) => {
    setFilterPlaces((prev) => prev.filter((p) => p !== placeIdOrName));
  };

  /** Split tags: sortable (locations/cities) vs fixed (Total all stores) */
  const { sortableTags, totalTag } = useMemo(() => {
    const total = selectedTags.find((t) => t.id === TOTAL_ALL_STORES_ID);
    const sortable = selectedTags.filter((t) => t.id !== TOTAL_ALL_STORES_ID);
    return { sortableTags: sortable, totalTag: total };
  }, [selectedTags]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIds = sortableTags.map((t) => t.id);
      const oldIndex = oldIds.indexOf(active.id as string);
      const newIndex = oldIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      const reorderedIds = arrayMove(oldIds, oldIndex, newIndex);
      const newFilterPlaces = totalTag
        ? [...reorderedIds, totalTag.id]
        : reorderedIds;
      setFilterPlaces(newFilterPlaces);
    },
    [sortableTags, totalTag, setFilterPlaces]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isAllSelected = useMemo(() => {
    const realSelected = filterPlaces.filter((p) => p !== TOTAL_ALL_STORES_ID);
    return (
      placeOptions.length > 0 && realSelected.length >= placeOptions.length
    );
  }, [placeOptions, filterPlaces]);

  const onSelectAll = () => {
    if (filterType === "city") {
      setFilterPlaces(isAllSelected ? [] : [...placeOptions]);
    } else {
      const allIds =
        dropLocations?.map((l) => l._id as string).filter(Boolean) ?? [];
      setFilterPlaces(isAllSelected ? [] : allIds);
    }
  };

  return (
    <div
      className={`bg-[#FFFAF8] -mx-4 sm:-mx-8 px-4 sm:px-8 -mt-4 sm:-mt-6 pt-4 sm:pt-6 backdrop-blur-sm bg-slate-50 ${sticky ? "sticky top-16 z-40" : ""}`}
    >
      <div
        ref={filtersRef}
        className="flex flex-col rounded-xl bg-[#C2E5FF] w-full p-2 sm:p-7 mb-1 sm:mb-10 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-4 sm:mb-0">
            Filter
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 flex-wrap">
          {/* 1. Filter type: Cities or Locations */}
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text whitespace-nowrap">
              Filter by:
            </label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {filterType === "city" ? "Cities" : "Locations"}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList className="rounded-xl z-[100]" placeholder={undefined}>
                <MenuItem
                  onClick={() => setFilterType("location")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Locations
                </MenuItem>
                <MenuItem
                  onClick={() => setFilterType("city")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Cities
                </MenuItem>
              </MenuList>
            </Menu>
          </div>

          {/* 2. Places multi-select - same style as Filter by */}
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text whitespace-nowrap">
              {filterType === "city" ? "Cities" : "Locations"}:
            </label>
            <Menu dismiss={{ itemPress: false }}>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm min-w-[140px] sm:min-w-[100px]">
                  {getPlaceLabel()}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList
                className="rounded-xl z-[100] max-h-[280px] overflow-y-auto"
                placeholder={undefined}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex rounded-md duration-200 hover:bg-[#EFF2F4] p-1 items-center space-x-2  cursor-pointer select-none"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelectAll();
                    }}
                    onKeyDown={(e) =>
                      e.key === "Enter" && (e.preventDefault(), onSelectAll())
                    }
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-transparent pointer-events-none"
                      checked={isAllSelected}
                      readOnly
                    />
                    <span className="text-gray-700  text-sm">
                      {filterType === "city" ? "All cities" : "All locations"}
                    </span>
                  </div>
                  {placeOptions.map((option) => {
                    const isSelected = selectedPlaceLabels.includes(option);
                    return (
                      <div
                        key={option}
                        role="button"
                        tabIndex={0}
                        className="flex duration-200 p-1 rounded-md hover:bg-[#EFF2F4] items-center space-x-2 mt-2 cursor-pointer select-none"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onSelectPlace(option);
                        }}
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          (e.preventDefault(), onSelectPlace(option))
                        }
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-transparent pointer-events-none"
                          checked={isSelected}
                          readOnly
                        />
                        <span className="text-gray-700 text-sm">{option}</span>
                      </div>
                    );
                  })}
                </div>
              </MenuList>
            </Menu>
          </div>

          {/* 3. Extra options - Total all stores as clear button */}
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text whitespace-nowrap">
              Extra:
            </label>
            <button
              type="button"
              className={`h-9 px-4 rounded-lg font-satoshi font-semibold text-sm shadow-sm transition-all duration-200 cursor-pointer hover:shadow-md active:scale-[0.98] ${
                filterPlaces.includes(TOTAL_ALL_STORES_ID)
                  ? "bg-droppie-oranje border-2 border-droppie-oranje text-white hover:bg-droppie-oranje-dark"
                  : "bg-white border-2 border-gray-300 text-gray-700 hover:border-droppie-oranje hover:text-droppie-oranje"
              }`}
              onClick={() =>
                setFilterPlaces((prev) =>
                  prev.includes(TOTAL_ALL_STORES_ID)
                    ? prev.filter((p) => p !== TOTAL_ALL_STORES_ID)
                    : [...prev, TOTAL_ALL_STORES_ID]
                )
              }
              aria-pressed={filterPlaces.includes(TOTAL_ALL_STORES_ID)}
              aria-label="Toggle Total all stores"
            >
              Total all stores
            </button>
          </div>
        </div>

        {/* Selected items as tags/chips - drag to reorder, click × to remove */}
        {selectedTags.length > 0 && (
          <div
            ref={filtersRef}
            className="flex flex-wrap items-center gap-2 mt-4"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortableTags.map((t) => t.id) as UniqueIdentifier[]}
                strategy={horizontalListSortingStrategy}
              >
                {sortableTags.map(({ id, label }) => (
                  <SortableChip
                    key={id}
                    id={id}
                    label={label}
                    onRemove={onRemoveTag}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {totalTag && (
              <button
                type="button"
                onClick={() => onRemoveTag(totalTag.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-100 border border-gray-200 text-sm font-satoshi text-gray-800 cursor-pointer hover:bg-gray-200 transition-colors"
                aria-label={`Remove ${totalTag.label}`}
              >
                {totalTag.label}
                <span className="ml-0.5 text-gray-500">×</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
